import React, { useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, Trash2, BookOpen } from "lucide-react";
import { allCategories } from "@/lib/workoutCategories";
import { CategoryPicker } from "@/components/CategoryPicker";

const TemplateCard = ({ w, categories, onReload }) => {
  const [cat, setCat] = useState(w.category);
  const saveCat = async (v) => {
    setCat(v);
    if (!v.trim()) return;
    try { await api.patch(`/workouts/templates/${w.id}/category`, { category: v }); onReload(); }
    catch { toast.error("Erro ao trocar categoria"); }
  };
  const remove = async () => {
    if (!window.confirm("Remover este treino da biblioteca?")) return;
    await api.delete(`/workouts/templates/${w.id}`); toast.success("Removido"); onReload();
  };
  return (
    <Card className="p-5 border border-slate-200" data-testid={`library-card-${w.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <h3 className="font-heading text-lg font-bold text-slate-900">{w.title}</h3>
          <p className="text-sm text-slate-500">{w.goal}</p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {w.generated_by_ai && <span className="text-xs bg-rose-50 text-rose-700 px-2 py-1 rounded-full font-semibold flex items-center gap-1"><Sparkles className="w-3 h-3"/>IA</span>}
          <Button variant="ghost" size="icon" onClick={remove} data-testid={`library-delete-${w.id}`}><Trash2 className="w-4 h-4 text-slate-400"/></Button>
        </div>
      </div>
      <div className="mb-3 max-w-xs">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Categoria</div>
        <CategoryPicker value={cat} onChange={saveCat} categories={categories} testid={`library-category-${w.id}`}/>
      </div>
      <div className="space-y-2">
        {(w.days || []).map((d, i) => (
          <div key={i} className="border-l-4 border-amber-500 pl-3">
            <div className="text-sm font-bold text-slate-900">Ficha {d.label} - {d.focus}</div>
            <div className="text-xs text-slate-600 mt-1">{(d.exercises || []).map(ex => ex.name).join(" · ")}</div>
          </div>
        ))}
      </div>
    </Card>
  );
};

export const WorkoutLibrary = ({ templates, onReload }) => {
  const [filter, setFilter] = useState("Todas");
  const categories = allCategories(templates);
  const used = categories.filter(c => templates.some(t => t.category === c));
  const shown = filter === "Todas" ? used : used.filter(c => c === filter);
  if (templates.length === 0) {
    return <p className="text-center text-slate-500 py-12" data-testid="library-empty"><BookOpen className="w-6 h-6 mx-auto mb-2"/>Nenhum treino na biblioteca. Crie um em Manual ou Gerar com IA escolhendo "Biblioteca de Treinos".</p>;
  }
  return (
    <div className="space-y-6" data-testid="workout-library">
      <div className="flex flex-wrap gap-2">
        {["Todas", ...used].map(c => (
          <button key={c} onClick={() => setFilter(c)} data-testid={`library-filter-${c}`}
            className={`text-xs px-3 py-1.5 rounded-full font-semibold transition-colors ${filter === c ? "bg-rose-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
            {c} {c !== "Todas" && <span className="opacity-70">({templates.filter(t => t.category === c).length})</span>}
          </button>
        ))}
      </div>
      {shown.map(c => (
        <section key={c} data-testid={`library-section-${c}`}>
          <h2 className="font-heading text-base md:text-lg font-bold text-slate-900 mb-3 uppercase tracking-wide">{c}</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {templates.filter(t => t.category === c).map(w => <TemplateCard key={w.id} w={w} categories={categories} onReload={onReload}/>)}
          </div>
        </section>
      ))}
    </div>
  );
};
